// Typed endpoint wrappers — one per /v1/* surface in api-design §3.
// Path shapes (including the literal `:activate` action segments) are
// preserved verbatim from api-design.

import { api } from './client';
import type {
  ActiveValueResponse,
  Agreement,
  AgreementSort,
  AgreementStatus,
  AgreementType,
  AgreementsByStatusRow,
  Allocation,
  Assignment,
  AuditEvent,
  Company,
  Credit,
  CreditType,
  DerivedAssignmentStatus,
  EndingSoonRow,
  Milestone,
  MyAssignment,
  PaginatedList,
  Person,
  Role,
  SessionResponse,
  SimpleListResponse,
  TeamUtilizationRow,
} from '@/types/domain';

// ---------- Auth ----------

export const authApi = {
  // Bootstrap probe AND the F-001 refresh probe. 401 from this endpoint
  // is the trigger for the full-page redirect to /v1/auth/login.
  session: () => api.get<SessionResponse>('/v1/auth/session', { retryable: false }),
  // Logout is idempotent per auth pattern §1.4. POSTed by the user menu.
  logout: () => api.post<void>('/v1/auth/logout'),
};

// `/v1/auth/login` is a server-driven 302 — never an XHR. Use
// `redirectToLogin()` (not a fetch) when the AppShell needs to navigate
// to the login flow.
export function redirectToLogin(): void {
  // window.location.href triggers a full-page navigation; the backend
  // 302 from /v1/auth/login carries the user to Google's authorize URL.
  window.location.href = '/v1/auth/login';
}

// ---------- Companies ----------

export interface CompanyListQuery {
  limit?: number;
  cursor?: string;
}

export const companiesApi = {
  list: (q: CompanyListQuery = {}) =>
    api.get<PaginatedList<Company>>('/v1/companies', { query: { ...q } }),
  get: (id: string) => api.get<Company>(`/v1/companies/${id}`),
  create: (body: { name: string }) => api.post<Company>('/v1/companies', body),
  update: (id: string, body: { name: string }) =>
    api.patch<Company>(`/v1/companies/${id}`, body),
};

// ---------- People ----------

export interface PeopleListQuery {
  role?: Role[];
  limit?: number;
  cursor?: string;
}

export const peopleApi = {
  list: (q: PeopleListQuery = {}) =>
    api.get<PaginatedList<Person>>('/v1/people', {
      query: {
        role: q.role,
        limit: q.limit,
        cursor: q.cursor,
      },
    }),
  get: (id: string) => api.get<Person>(`/v1/people/${id}`),
  create: (body: {
    name: string;
    email: string;
    cost_rate?: string | null;
    role?: Role;
  }) => api.post<Person>('/v1/people', body),
  update: (
    id: string,
    body: Partial<{
      name: string;
      email: string;
      cost_rate: string | null;
      role: Role;
    }>,
  ) => api.patch<Person>(`/v1/people/${id}`, body),
};

// ---------- Agreements ----------

export interface AgreementListQuery {
  status?: AgreementStatus[];
  client_id?: string;
  type?: AgreementType[];
  sort?: AgreementSort;
  limit?: number;
  cursor?: string;
}

export interface AgreementCreateBody {
  client_id: string;
  project_name: string;
  sow_id: string;
  amendment?: number;
  type: AgreementType;
  start_date: string;
  end_date?: string | null;
  total_value?: string | null;
  estimated_value?: string | null;
  description?: string | null;
  owner_person_id?: string;
}

export interface AgreementPatchBody {
  start_date?: string;
  end_date?: string | null;
  total_value?: string | null;
  estimated_value?: string | null;
  description?: string | null;
  owner_person_id?: string;
}

export interface AmendmentCreateBody {
  type: AgreementType;
  start_date: string;
  end_date?: string | null;
  total_value?: string | null;
  estimated_value?: string | null;
  description?: string | null;
  owner_person_id: string;
}

export const agreementsApi = {
  list: (q: AgreementListQuery = {}) =>
    api.get<PaginatedList<Agreement>>('/v1/agreements', {
      query: {
        status: q.status,
        client_id: q.client_id,
        type: q.type,
        sort: q.sort,
        limit: q.limit,
        cursor: q.cursor,
      },
    }),
  get: (id: string) => api.get<Agreement>(`/v1/agreements/${id}`),
  create: (body: AgreementCreateBody) => api.post<Agreement>('/v1/agreements', body),
  update: (id: string, body: AgreementPatchBody) =>
    api.patch<Agreement>(`/v1/agreements/${id}`, body),
  // Action endpoints — never auto-retry per frontend-design §6.
  activate: (id: string) => api.post<Agreement>(`/v1/agreements/${id}:activate`, {}),
  complete: (id: string) => api.post<Agreement>(`/v1/agreements/${id}:complete`, {}),
  revert: (id: string) => api.post<Agreement>(`/v1/agreements/${id}:revert`, {}),
  archive: (id: string) => api.post<Agreement>(`/v1/agreements/${id}:archive`, {}),
  amend: (id: string, body: AmendmentCreateBody) =>
    api.post<Agreement>(`/v1/agreements/${id}:amend`, body),
  auditEvents: (
    id: string,
    q: { event_name?: string[]; actor_person_id?: string; from?: string; to?: string; limit?: number; cursor?: string } = {},
  ) =>
    api.get<PaginatedList<AuditEvent>>(`/v1/agreements/${id}/audit-events`, {
      query: { ...q },
    }),
};

// ---------- Assignments & Credits ----------

export interface AssignmentCreateBody {
  person_id: string;
  role?: string | null;
  bill_rate: string;
  cost_rate: string;
  allocation_pct: number;
  start_date: string;
  end_date?: string | null;
}

export interface AssignmentPatchBody {
  role?: string | null;
  bill_rate?: string;
  cost_rate?: string;
  allocation_pct?: number;
  start_date?: string;
  end_date?: string | null;
}

export const assignmentsApi = {
  listByAgreement: (
    agreementId: string,
    q: { limit?: number; cursor?: string } = {},
  ) =>
    api.get<PaginatedList<Assignment>>(
      `/v1/agreements/${agreementId}/assignments`,
      { query: q },
    ),
  get: (id: string) => api.get<Assignment>(`/v1/assignments/${id}`),
  create: (agreementId: string, body: AssignmentCreateBody) =>
    api.post<Assignment>(`/v1/agreements/${agreementId}/assignments`, body),
  update: (id: string, body: AssignmentPatchBody) =>
    api.patch<Assignment>(`/v1/assignments/${id}`, body),
  // Cascade-on-Credits per api-design §3 / F-003.
  delete: (id: string) => api.delete<void>(`/v1/assignments/${id}`),
};

export interface CreditCreateBody {
  person_id: string;
  credit_type: CreditType;
  percentage: number;
}

export interface CreditPatchBody {
  credit_type?: CreditType;
  percentage?: number;
}

export const creditsApi = {
  listByAssignment: (assignmentId: string) =>
    api.get<PaginatedList<Credit>>(`/v1/assignments/${assignmentId}/credits`),
  create: (assignmentId: string, body: CreditCreateBody) =>
    api.post<Credit>(`/v1/assignments/${assignmentId}/credits`, body),
  update: (id: string, body: CreditPatchBody) =>
    api.patch<Credit>(`/v1/credits/${id}`, body),
  delete: (id: string) => api.delete<void>(`/v1/credits/${id}`),
};

// ---------- Milestones & Allocations (FP only) ----------

export const milestonesApi = {
  listByAgreement: (agreementId: string) =>
    api.get<PaginatedList<Milestone>>(`/v1/agreements/${agreementId}/milestones`),
  create: (
    agreementId: string,
    body: { milestone_name: string; expected_date: string; amount: string },
  ) => api.post<Milestone>(`/v1/agreements/${agreementId}/milestones`, body),
  update: (
    id: string,
    body: Partial<{ milestone_name: string; expected_date: string; amount: string }>,
  ) => api.patch<Milestone>(`/v1/milestones/${id}`, body),
  delete: (id: string) => api.delete<void>(`/v1/milestones/${id}`),
};

export const allocationsApi = {
  listByMilestone: (milestoneId: string) =>
    api.get<PaginatedList<Allocation>>(`/v1/milestones/${milestoneId}/allocations`),
  create: (milestoneId: string, body: { person_id: string; amount: string }) =>
    api.post<Allocation>(`/v1/milestones/${milestoneId}/allocations`, body),
  update: (id: string, body: Partial<{ amount: string }>) =>
    api.patch<Allocation>(`/v1/allocations/${id}`, body),
  delete: (id: string) => api.delete<void>(`/v1/allocations/${id}`),
};

// ---------- My Assignments ----------

export const meApi = {
  myAssignments: (
    q: { derived_status?: DerivedAssignmentStatus[]; limit?: number; cursor?: string } = {},
  ) =>
    api.get<PaginatedList<MyAssignment>>('/v1/me/assignments', {
      query: { ...q },
    }),
};

// ---------- Dashboard ----------

export const dashboardApi = {
  agreementsByStatus: () =>
    api.get<SimpleListResponse<AgreementsByStatusRow>>(
      '/v1/dashboard/agreements-by-status',
    ),
  activeValue: () => api.get<ActiveValueResponse>('/v1/dashboard/active-value'),
  teamUtilization: (q: { limit?: number; cursor?: string } = {}) =>
    api.get<PaginatedList<TeamUtilizationRow>>('/v1/dashboard/team-utilization', {
      query: { ...q },
    }),
  endingSoon: (q: { limit?: number; cursor?: string } = {}) =>
    api.get<PaginatedList<EndingSoonRow>>('/v1/dashboard/ending-soon', {
      query: { ...q },
    }),
};
